import React, { useEffect, useRef, useState } from "react";
import "./styles.sass";
import assets from "../../assets";
import { useSingleton } from "../../hooks/use-singleton";
import { AdminQuizPlay } from "../admin-quiz-play";
import { AdminLeaderboard } from "../admin-leader-board";
import { AdminQuizHistory } from "../admin-quiz-history";
import { CommonPopup } from "../pop-up";
import { AdminRoundResult } from "../admin-round-results";

export const AdminLiveQuizRoom = ({ roomCode, setRoomCode }) => {
  const socketRef = useRef(null);
  const { user, mail, role } = useSingleton();
  const [showHistory, setShowHistory] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [quizStarted, setQuizStarted] = useState(false);
  const [quizEnded, setQuizEnded] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [copied, setCopied] = useState(false);
  const [showRoundResult, setShowRoundResult] = useState(false);
  const [roundResultData, setRoundResultData] = useState(null);

  useEffect(() => {
    // websocket connection
    const socket = new WebSocket(
      `wss://rovianai.com/secure-api/room/ws/${roomCode}?role=host&mail=${encodeURIComponent(mail)}`,
    );

    socketRef.current = socket;

    socket.onopen = () => {
      console.log("WebSocket Connected");

      // optional join event
      socket.send(
        JSON.stringify({
          event: "join_host",
          room_code: roomCode,
        }),
      );
    };

    socket.onmessage = (message) => {
      const data = JSON.parse(message.data);

      console.log("WS EVENT:", data);

      switch (data.event) {
        case "lobby_update":
          setParticipants(data.participants || []);
          break;

        case "quiz_started":
          setQuizStarted(true);
          break;

        case "question":
          setCurrentQuestion(data);
          break;

        case "round_result":
          setLeaderboard(data.leaderboard || []);

          setCurrentQuestion((prev) => ({
            ...prev,
            correctAnswer: data.correct_answer,
            correctText: data.correct_text,
            showAnswer: true,
          }));

          // ✅ MOVE HERE
          setRoundResultData(data);
          setShowRoundResult(true);

          setTimeout(() => {
            setShowRoundResult(false);
          }, 5000);

          break;

        case "quiz_ended":
          setLeaderboard(data.final_scores || []);
          setQuizEnded(true);
          break;

        default:
          break;
      }
    };

    socket.onerror = (error) => {
      console.log("WebSocket Error", error);
    };

    socket.onclose = () => {
      console.log("WebSocket Closed");
    };

    return () => {
      socket.close();
    };
  }, [roomCode]);

  const resetQuizFlow = () => {
    setShowHistory(false);
    setQuizStarted(false);
    setQuizEnded(false);
    setParticipants([]);
    setCurrentQuestion(null);
    setLeaderboard([]);
    setRoomCode("");
  };

  const startQuiz = () => {
    socketRef.current?.send(
      JSON.stringify({
        event: "start_quiz",
      }),
    );
  };

  if (showHistory) {
    return <AdminQuizHistory />;
  }
  if (quizEnded) {
    return (
      <AdminLeaderboard
        leaderboard={leaderboard}
        roomCode={roomCode}
        isLiveMode={true}
        setShowHistory={setShowHistory}
        resetQuizFlow={resetQuizFlow}
      />
    );
  }

  if (quizStarted) {
    return showRoundResult ? (
      <CommonPopup
        open={showRoundResult}
        onClose={() => setShowRoundResult(false)}
        url={true}
      >
        <AdminRoundResult data={roundResultData} />
      </CommonPopup>
    ) : (
      <AdminQuizPlay
        currentQuestion={currentQuestion}
        leaderboard={leaderboard}
        socketRef={socketRef}
      />
    );
  }

  return (
    <section className="live-room">
      <section className="container">
        <h2>Live Quiz Room</h2>
        <p className="sub">Share this code with your class to let them join.</p>

        {/* Room Code Card */}
        <section className="room-card">
          <section className="code-section">
            <span className="label">ROOM CODE</span>
            {roomCode && (
              <section className="code">
                {roomCode}
                <img
                  src={assets.ic_copy}
                  alt="clip-board"
                  className={`copy-btn ${copied ? "copied" : ""}`}
                  onClick={() => {
                    navigator.clipboard.writeText(roomCode);

                    setCopied(true);

                    setTimeout(() => {
                      setCopied(false);
                    }, 2000);
                  }}
                />
                {copied && <span className="copied-text">Copied!</span>}
              </section>
            )}
          </section>

          <button className="start-btn" onClick={startQuiz}>
            Start Quiz <span>▶</span>
          </button>
        </section>

        {/* Students */}
        <section className="students-header">
          <h4>Joined Students</h4>
          <span className="online">
            ● {participants.length} students online
          </span>
        </section>

        <section className="students-grid">
          {participants?.length > 0 ? (
            participants.map((s, i) => (
              <section className="student-card" key={i}>
                <section className="avatar">
                  {s.nickname?.charAt(0)?.toUpperCase()}
                </section>

                <p>{s.nickname}</p>
              </section>
            ))
          ) : (
            <section className="student-card empty waiting-card">
              <section className="avatar">👤</section>

              <p>Waiting for students to join...</p>
            </section>
          )}
        </section>
      </section>
    </section>
  );
};
